import React, { useState } from 'react';
import { makeStyles, Paper, TextField } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { UserModelDto } from '.generated/api';
import { buttonPrimaryColor } from 'theme/theme';
import clsx from 'clsx';

const useStyles = makeStyles(() => ({
  paperRoot: {
    position: 'absolute',
    zIndex: 9999,
    padding: '0',
    top: '50px',
    width: '100%',
    borderRadius: '0 0 4px 4px',
    boxShadow: 'none',
    borderTop: 'none',
    borderRight: `2px solid ${buttonPrimaryColor()}`,
    borderBottom: `2px solid ${buttonPrimaryColor()}`,
    borderLeft: `2px solid ${buttonPrimaryColor()}`,
  },
  container: {
    position: 'relative',
  },
  fieldWithDD: {
    borderColor: buttonPrimaryColor(),
    '& .MuiOutlinedInput-root': {
      fieldset: {
        borderColor: buttonPrimaryColor(),
      },
    },
  },
  dropdown: {
    paddingRight: '0',
    paddingLeft: '0',
    margin: '0',
  },
  dropdownOption: {
    display: 'block',
    listStyle: 'none',
    padding: '0.5rem 0.8rem',
    cursor: 'pointer',
    color: '#000',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

type UserAutocompleteProps = {
  onSelectOption: (user: UserModelDto) => void;
  onChangeName: (name: string) => void;
  label: string;
  value: string;
};

const UserAutocomplete: React.FC<UserAutocompleteProps> = (props) => {
  const { onSelectOption, onChangeName, label, value } = props;
  const classes = useStyles();
  const [filteredUsers, setFilteredUsers] = useState<UserModelDto[]>([]);
  const [isPopoverVisible, setisPopoverVisible] = useState(false);
  const organization = useSelector(
    (state: ApplicationState) => state.organization?.organization,
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = e.target;
    onChangeName(value);

    const tempUsers: UserModelDto[] = [];
    setFilteredUsers(tempUsers);
    if (value.length > 2) {
      organization?.users?.forEach((user) => {
        if (user.name.toLowerCase().indexOf(value.toLowerCase()) > -1) {
          tempUsers.push(user);
        }
      });
      setFilteredUsers(tempUsers);
      if (tempUsers.length > 0) {
        setisPopoverVisible(true);
      }
    }
  };

  const handleClickAway = () => {
    setisPopoverVisible(false);
  };

  const handleSelect = (user: UserModelDto) => {
    onSelectOption(user);

    setisPopoverVisible(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={classes.container}>
        <form
          noValidate
          onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
            // disable form submit here since the form tag is only used to disable autocomplete in browser.
            event.preventDefault();
          }}
        >
          <TextField
            type="text"
            variant="outlined"
            margin="none"
            fullWidth
            label={label}
            value={value}
            onChange={handleChange}
            className={clsx(
              filteredUsers.length > 0 ? classes.fieldWithDD : '',
            )}
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
        </form>

        {isPopoverVisible && filteredUsers.length > 0 && (
          <Paper className={classes.paperRoot} id="autocompleteDD">
            <ul className={classes.dropdown}>
              {filteredUsers?.map((user, index) => {
                return (
                  <li
                    className={classes.dropdownOption}
                    key={index}
                    onClick={() => handleSelect(user)}
                  >
                    {user?.name} - {user?.userEmail}
                  </li>
                );
              })}
            </ul>
          </Paper>
        )}
      </div>
    </ClickAwayListener>
  );
};

export { UserAutocomplete };
